import React, {
    CSSProperties,
    FC,
    ReactNode,
    useState,
    Children,
    isValidElement,
    ReactElement,
    useEffect,
    useMemo,
    useCallback,
} from "react";
import arrowDown from "../../../assets/arrowDown.svg";
import cross from "../../../assets/cancelGreey.svg";
import "./SelectDropdown.css";
import Search from "../../UiLib/Search/Search";

interface SelectDropdownProps {
    className?: string;
    errorMessage?: string;
    optionsContainerStyle?: CSSProperties;
    optionsListStyle?: CSSProperties;
    children: ReactNode;
    enableSearch?: boolean;
    showResetButton?: boolean;
    onReset?: () => void;
    onOpen?: () => void;
}

interface SelectedProps {
    children: ReactNode;
}

interface OptionProps {
    children: ReactNode;
    textForSearch?: string;
    value?: string;
}

const SelectDropdown: FC<SelectDropdownProps> & {
    Selected: FC<SelectedProps>;
    Option: FC<OptionProps>;
} = ({
    children,
    errorMessage,
    optionsContainerStyle,
    optionsListStyle,
    enableSearch,
    showResetButton,
    onReset,
    onOpen,
    className,
}) => {
    const [openDropdown, setOpenDropdown] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>("");
    const [selectedElement, setSelectedElement] = useState<ReactNode>();

    // Мемоизированные обработчики событий
    const toggleDropdown = useCallback(() => {
        setOpenDropdown(prev => !prev);
        onOpen?.();
    }, [onOpen]);

    const closeDropdown = useCallback(() => {
        setOpenDropdown(false);
    }, []);

    const handleReset = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        onReset?.();
    }, [onReset]);

    const handleOptionClick = useCallback(() => {
        setOpenDropdown(false);
        setSearchText("");
    }, []);

    // Оптимизированное извлечение опций и выбранного элемента
    const { options, selected } = useMemo(() => {
        const opts: ReactElement<OptionProps>[] = [];
        let sel: ReactNode = null;

        Children.forEach(children, (child) => {
            if (isValidElement(child)) {
                if (child.type === SelectDropdown.Selected) {
                    sel = child;
                } else if (child.type === SelectDropdown.Option) {
                    const childTyped = child as ReactElement<OptionProps>;
                    
                    if (!enableSearch) {
                        opts.push(childTyped);
                        return;
                    }
                    
                    if (
                        !searchText ||
                        (childTyped.props.textForSearch &&
                        childTyped.props.textForSearch.toLowerCase().includes(searchText.toLowerCase()))
                    ) {
                        opts.push(childTyped);
                    }
                }
            }
        });

        return { options: opts, selected: sel };
    }, [children, searchText, enableSearch]);

    // Установка выбранного элемента только при изменении
    useEffect(() => {
        setSelectedElement(selected);
    }, [selected]);

    return (
        <div className={`select-dropdown-container ${className ?? ""}`}>
            <div
                className={`container_pointer ${errorMessage ? "div_with_error" : ""}`}
                onClick={toggleDropdown}
                role="combobox"
                aria-expanded={openDropdown}
                aria-haspopup="listbox"
            >
                <div className="selected-option">{selectedElement}</div>
                <div className="dropdown-controls">
                    {showResetButton && (
                        <img
                            alt="Reset selection"
                            src={cross}
                            onClick={handleReset}
                            className="reset-button"
                        />
                    )}
                    <img
                        alt="Toggle dropdown"
                        src={arrowDown}
                        className={`dropdown-arrow ${openDropdown ? "dropdown-arrow-open" : ""}`}
                    />
                </div>
            </div>
            
            {openDropdown && (
                <>
                    <div className="dropdown-wrapper">
                        <div className="dropdown_overview" style={optionsContainerStyle}>
                            {enableSearch && (
                                <Search
                                    value={searchText}
                                    changeSearchValue={setSearchText}
                                    hideSubmitButton
                                />
                            )}
                            <div 
                                className="dropdown_overview-container" 
                                style={optionsListStyle}
                                role="listbox"
                            >
                                {options.map((option, index) => (
                                    <div
                                        key={option.props.value || option.key || index}
                                        className="dropdown_option"
                                        onClick={handleOptionClick}
                                        role="option"
                                    >
                                        {option.props.children}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div
                        className="full-screen-overlay_dropdown"
                        onClick={closeDropdown}
                        role="presentation"
                    />
                </>
            )}
        </div>
    );
};

SelectDropdown.Selected = ({ children }: SelectedProps) => <>{children}</>;
SelectDropdown.Option = ({ children }: OptionProps) => <>{children}</>;

export default SelectDropdown;